import React from "react";
import {
  Calendar, Check, Video, MapPin, Eye, UserX,
  EyeOff, MoreHorizontal, AlertTriangle, Trash2, FileText, StickyNote, X,
  CheckCircle2, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ROLES,
  CORE_TEAM,
  isStudioJob,
  isWeddingJob,
  isWeddingFair,
  hasJobSheet,
  getMinStaff,
  extractVenue,
  isSheetOverdue,
} from "@/utils/constants";
import { MobileTeamEditDialog } from "@/components/MobileTeamEditDialog";

export const RotaListView = ({
  sortedDates,
  groupedJobs,
  filteredJobs,
  teamMembers,
  editMode,
  unavailableByDate,
  recentChangeDates,
  onAssignmentChange,
  onVideoBoothChange,
  onHideJob,
  onDeleteJob,
  onConfirmStaffing,
  onOpenJobSheet,
  sheetStatuses,
  getConflict,
  formatDate,
  getDisplayTitle,
  notesByDate,
  onDeleteNote,
}) => {
  const [teamEditJobId, setTeamEditJobId] = React.useState(null);
  const teamEditJob = teamEditJobId ? filteredJobs.find(j => j.id === teamEditJobId) : null;
  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block table-container bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f3f4f6]">
              <TableHead className="w-[200px] font-semibold text-[#605e5e] sticky left-0 bg-[#f3f4f6] z-10">Event</TableHead>
              <TableHead className="w-[120px] font-semibold text-[#605e5e]">Venue</TableHead>
              <TableHead className="w-[50px] text-center font-semibold text-[#605e5e]" title="Video Booth">
                <Video className="h-3.5 w-3.5 mx-auto" />
              </TableHead>
              {ROLES.map(role => (
                <TableHead key={role} className="w-[95px] text-center font-semibold text-[#605e5e]">
                  {role}
                </TableHead>
              ))}
              {editMode && <TableHead className="w-[40px] text-center font-semibold text-[#605e5e]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedDates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={ROLES.length + (editMode ? 4 : 3)} className="text-center py-12">
                  <div className="empty-state">
                    <Calendar className="h-12 w-12 text-[#8d8b8b] mb-4" />
                    <p className="text-[#605e5e] font-medium">No upcoming jobs</p>
                    <p className="text-sm text-[#8d8b8b] mt-1">
                      {editMode ? "Add a job or sync your calendar to get started" : "No jobs to display"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedDates.map((date, dateIdx) => (
                <React.Fragment key={`group-${date}`}>
                  {dateIdx > 0 && (
                    <TableRow>
                      <TableCell colSpan={ROLES.length + (editMode ? 4 : 3)} className="p-0 h-3 bg-[#f3f4f6] border-none"></TableCell>
                    </TableRow>
                  )}
                  
                  <TableRow id={`date-${date}`} className="border-none">
                    <TableCell colSpan={ROLES.length + (editMode ? 4 : 3)} className="py-2.5 px-4 sticky left-0 bg-[#605e5e] border-none">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-heading text-base text-white">{formatDate(date)}</span>
                        <Badge className="bg-white/15 text-white/80 text-[10px] px-1.5 py-0 border-none" data-testid={`date-summary-${date}`}>
                          {(groupedJobs[date]?.length || 0)} job{(groupedJobs[date]?.length || 0) !== 1 ? 's' : ''}
                          {` · ${CORE_TEAM.length - (unavailableByDate[date]?.filter(p => CORE_TEAM.includes(p)).length || 0)}/${CORE_TEAM.length} available`}
                        </Badge>
                        {recentChangeDates.includes(date) && (
                          <Badge className="bg-white/20 text-white text-[10px] px-1.5 py-0 border-none">
                            recently changed
                          </Badge>
                        )}
                        {unavailableByDate[date]?.length > 0 && (
                          <div className="flex items-center gap-1">
                            {unavailableByDate[date].map((person) => (
                              <Badge key={person} className="text-[10px] bg-red-400/30 text-white border-none">
                                <UserX className="h-2 w-2 mr-1" />
                                {person}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {notesByDate?.[date]?.length > 0 && (
                          <div className="flex items-center gap-1">
                            {notesByDate[date].map((note) => (
                              <Badge key={note.id} className="text-[10px] bg-teal-400/30 text-white border-none" data-testid={`note-badge-${note.id}`}>
                                <StickyNote className="h-2 w-2 mr-1" />
                                {note.title}
                                {editMode && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); onDeleteNote(note.id); }}
                                    className="ml-1 hover:text-red-300 transition-colors"
                                    data-testid={`note-delete-${note.id}`}
                                  >
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                )}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {(groupedJobs[date]?.length || 0) === 0 ? (
                    <TableRow>
                      <TableCell colSpan={ROLES.length + (editMode ? 4 : 3)} className="text-center py-4 text-sm text-[#8d8b8b] italic">
                        No jobs
                      </TableCell>
                    </TableRow>
                  ) : groupedJobs[date].map(job => {
                    const minStaff = getMinStaff(job.event_name || job.title);
                    const assigned = ROLES.filter(r => job.assignments?.[r]).length;
                    const understaffed = minStaff > 0 && assigned < minStaff && !job.staffing_confirmed;

                    return (
                      <TableRow 
                        key={job.id} 
                        className={`rota-row transition-colors ${job.is_hidden ? 'opacity-40 bg-gray-100' : isStudioJob(job) ? 'bg-violet-50/60 hover:bg-violet-50' : 'hover:bg-[#f9fafb]'}`}
                        data-testid={`job-row-${job.id}`}
                      >
                        <TableCell className={`font-medium sticky left-0 z-10 ${job.is_hidden ? 'bg-gray-100' : isStudioJob(job) ? 'bg-violet-50/60' : 'bg-white'}`}>
                          <div className="flex items-center gap-2">
                            {job.is_hidden && <EyeOff className="h-3 w-3 text-gray-400" />}
                            {isStudioJob(job) && (
                              <span className="flex-shrink-0 w-1.5 h-4 rounded-full bg-violet-400"></span>
                            )}
                            {understaffed && (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-100 text-red-600" data-testid={`understaffed-${job.id}`}>
                                      <AlertTriangle className="h-3 w-3" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="text-xs">
                                    Needs {minStaff} people, only {assigned} assigned
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <p className={`text-sm ${job.is_hidden ? 'text-gray-400 line-through' : 'text-[#1f2937]'}`}>
                              {getDisplayTitle(job)}
                              {isWeddingFair(job) && (
                                <span className="ml-1.5 inline-block align-middle text-[9px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full" data-testid={`fair-badge-${job.id}`}>
                                  Wedding Fair
                                </span>
                              )}
                              {isStudioJob(job) && job.time && (
                                <span className="ml-1.5 text-xs text-violet-500 font-medium">{job.time}</span>
                              )}
                            </p>
                            {job.studio_type && (
                              <span className="flex-shrink-0 text-[10px] font-medium bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`studio-type-${job.id}`}>
                                {job.studio_type.replace(' Photoshoot', '').replace(' Studio Hire', '')}
                              </span>
                            )}
                            {hasJobSheet(job) && (() => {
                              const sheetSent = sheetStatuses?.[job.id]?.send_status === 'sent';
                              const overdue = !sheetSent && isSheetOverdue(job.date);
                              return (
                                <button
                                  onClick={() => onOpenJobSheet(job.id)}
                                  className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors ${sheetSent ? 'bg-green-600 text-white hover:bg-green-700' : overdue ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' : 'bg-[#fcd34d] text-black hover:bg-[#fbbf24]'}`}
                                  data-testid={`job-sheet-btn-${job.id}`}
                                >
                                  {sheetSent ? <CheckCircle2 className="h-3 w-3" /> : overdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                  {sheetSent ? 'Sheet Sent' : overdue ? 'Sheet Overdue' : 'Sheet Pending'}
                                </button>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-[#6b7280]">
                            {job.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{extractVenue(job.location)}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {editMode ? (
                            <Checkbox 
                              checked={job.has_video_booth}
                              onCheckedChange={(checked) => onVideoBoothChange(job.id, checked)}
                              data-testid={`video-booth-${job.id}`}
                            />
                          ) : (
                            job.has_video_booth && (
                              <Check className="h-4 w-4 mx-auto text-[#8DA399]" />
                            )
                          )}
                        </TableCell>
                        {ROLES.map(role => {
                          const conflict = getConflict(job.id, job.assignments?.[role], job.date);
                          return (
                          <TableCell key={role} className="text-center">
                            {editMode ? (
                              <div className="relative">
                                <Select
                                  value={job.assignments?.[role] || "unassigned"}
                                  onValueChange={(value) => onAssignmentChange(job.id, role, value === "unassigned" ? null : value)}
                                >
                                  <SelectTrigger 
                                    className={`member-select text-xs ${
                                      conflict
                                        ? 'bg-amber-100 text-amber-900 border-amber-400 ring-1 ring-amber-300'
                                        : job.assignments?.[role] 
                                          ? 'bg-[#8DA399] text-white border-[#8DA399]' 
                                          : 'bg-gray-100 text-[#6b7280] border-gray-200'
                                    }`}
                                    data-testid={`assignment-${job.id}-${role}`}
                                  >
                                    <SelectValue>
                                      {job.assignments?.[role] 
                                        ? teamMembers.find(m => m.id === job.assignments[role])?.name || "Select"
                                        : "\u2014"
                                      }
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">{"\u2014"} Unassigned {"\u2014"}</SelectItem>
                                    {teamMembers.map(member => (
                                      <SelectItem key={member.id} value={member.id}>
                                        {member.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {conflict && (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <AlertTriangle className="h-3 w-3 text-amber-600 absolute -top-1 -right-1" data-testid={`conflict-${job.id}-${role}`} />
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        {conflict.memberName} has {conflict.count} other job{conflict.count > 1 ? 's' : ''} this date
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            ) : (
                              <div className="relative inline-block">
                                <span className={`inline-block px-2 py-1 rounded text-xs ${
                                  conflict
                                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                    : job.assignments?.[role] 
                                      ? 'bg-[#8DA399] text-white' 
                                      : 'bg-gray-100 text-[#6b7280]'
                                }`}>
                                  {job.assignments?.[role] 
                                    ? teamMembers.find(m => m.id === job.assignments[role])?.name || "\u2014"
                                    : "\u2014"
                                  }
                                </span>
                                {conflict && (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <AlertTriangle className="h-3 w-3 text-amber-600 absolute -top-1 -right-1" />
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        {conflict.memberName} has {conflict.count} other job{conflict.count > 1 ? 's' : ''} this date
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            )}
                          </TableCell>
                          );
                        })}
                        {editMode && (
                          <TableCell className="text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  data-testid={`job-actions-${job.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4 text-[#8d8b8b]" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {getMinStaff(job.event_name || job.title) > 0 && (
                                  <DropdownMenuItem
                                    onClick={() => onConfirmStaffing(job.id)}
                                    data-testid={`confirm-staffing-${job.id}`}
                                  >
                                    {job.staffing_confirmed ? (
                                      <><AlertTriangle className="h-4 w-4 mr-2 text-amber-500" /> Unconfirm staff</>
                                    ) : (
                                      <><Check className="h-4 w-4 mr-2 text-green-600" /> Confirm staffing</>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => onHideJob(job.id)}
                                  data-testid={`hide-job-${job.id}`}
                                >
                                  {job.is_hidden ? (
                                    <><Eye className="h-4 w-4 mr-2 text-green-600" /> Show</>
                                  ) : (
                                    <><EyeOff className="h-4 w-4 mr-2" /> Hide</>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => onDeleteJob(job.id)}
                                  className="text-red-600 focus:text-red-600"
                                  data-testid={`delete-job-${job.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3" data-testid="mobile-card-view">
        {filteredJobs.length === 0 ? (
          <div className="text-center py-12 text-[#8d8b8b]">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No jobs found</p>
          </div>
        ) : (
          sortedDates.map(date => (
            <div key={`m-${date}`} className="rounded-lg overflow-hidden border border-[#e5e7eb]">
              <div id={`date-${date}`} className="bg-[#605e5e] px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-sm text-white">{formatDate(date)}</span>
                  <Badge className="bg-white/15 text-white/80 text-[10px] px-1.5 py-0 border-none">
                    {(groupedJobs[date]?.length || 0)} job{(groupedJobs[date]?.length || 0) !== 1 ? 's' : ''}
                    {` · ${CORE_TEAM.length - (unavailableByDate[date]?.filter(p => CORE_TEAM.includes(p)).length || 0)}/${CORE_TEAM.length}`}
                  </Badge>
                  {recentChangeDates.includes(date) && (
                    <Badge className="bg-white/20 text-white text-[10px] px-1.5 py-0 border-none">
                      changed
                    </Badge>
                  )}
                </div>
                {unavailableByDate[date]?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {unavailableByDate[date].map((person) => (
                      <Badge key={person} variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                        <UserX className="h-2 w-2 mr-0.5" />{person}
                      </Badge>
                    ))}
                  </div>
                )}
                {notesByDate?.[date]?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {notesByDate[date].map((note) => (
                      <Badge key={note.id} className="text-[10px] bg-teal-100 text-teal-800 border-none">
                        <StickyNote className="h-2 w-2 mr-0.5" />{note.title}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {(groupedJobs[date]?.length || 0) === 0 ? (
                <div className="p-3 text-center text-sm text-[#8d8b8b] italic bg-white">No jobs</div>
              ) : groupedJobs[date].map(job => {
                const minStaff = getMinStaff(job.event_name || job.title);
                const assigned = ROLES.filter(r => job.assignments?.[r]).length;
                const understaffed = minStaff > 0 && assigned < minStaff && !job.staffing_confirmed;
                const members = ROLES.map(r => job.assignments?.[r]).filter(Boolean).map(id => teamMembers.find(m => m.id === id)?.name).filter(Boolean);

                return (
                  <div
                    key={`m-${job.id}`}
                    onClick={() => editMode && !job.is_unavailable && setTeamEditJobId(job.id)}
                    className={`p-3 border-b border-[#e5e7eb] ${editMode && !job.is_unavailable ? 'cursor-pointer active:bg-gray-50' : ''} ${job.is_hidden ? 'opacity-40 bg-white' : isStudioJob(job) ? 'bg-violet-50/60 border-l-2 border-l-violet-400' : 'bg-white'}`}
                    data-testid={`mobile-job-${job.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {understaffed && (
                        <span className="flex-shrink-0 mt-0.5 flex items-center justify-center h-4 w-4 rounded-full bg-red-100 text-red-600">
                          <AlertTriangle className="h-2.5 w-2.5" />
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${job.is_hidden ? 'text-gray-400 line-through' : 'text-[#1f2937]'}`}>
                            {getDisplayTitle(job)}
                            {isWeddingFair(job) && (
                              <span className="ml-1.5 inline-block align-middle text-[9px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full" data-testid={`mobile-fair-badge-${job.id}`}>
                                Wedding Fair
                              </span>
                            )}
                            {isStudioJob(job) && job.time && (
                              <span className="ml-1.5 text-xs text-violet-500 font-medium">{job.time}</span>
                            )}
                          </p>
                          {job.studio_type && (
                            <span className="flex-shrink-0 text-[10px] font-medium bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              {job.studio_type.replace(' Photoshoot', '').replace(' Studio Hire', '')}
                            </span>
                          )}
                          {job.has_video_booth && (
                            <Video className="h-3 w-3 text-[#8DA399] flex-shrink-0" />
                          )}
                          {isWeddingJob(job) && (() => {
                            const sheetSent = sheetStatuses?.[job.id]?.send_status === 'sent';
                            const overdue = !sheetSent && isSheetOverdue(job.date);
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); onOpenJobSheet(job.id); }}
                                className={`flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors ${sheetSent ? 'bg-green-600 text-white hover:bg-green-700' : overdue ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' : 'bg-[#fcd34d] text-black hover:bg-[#fbbf24]'}`}
                                data-testid={`mobile-job-sheet-btn-${job.id}`}
                              >
                                {sheetSent ? <CheckCircle2 className="h-3.5 w-3.5" /> : overdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                              </button>
                            );
                          })()}
                        </div>
                        {job.location && (
                          <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{extractVenue(job.location)}</span>
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {members.length > 0 ? members.map((name) => (
                            <Badge key={name} className="bg-[#8DA399] text-white text-[10px] px-1.5 py-0">
                              {name}
                            </Badge>
                          )) : (
                            <span className="text-[10px] text-[#8d8b8b]">No team assigned</span>
                          )}
                          {understaffed && (
                            <span className="text-[10px] text-red-500">{assigned}/{minStaff} needed</span>
                          )}
                          {editMode && !job.is_unavailable && (
                            <span className="text-[10px] text-[#8DA399] font-medium ml-auto" data-testid={`mobile-edit-team-hint-${job.id}`}>Tap to edit team</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <MobileTeamEditDialog
        job={teamEditJob}
        teamMembers={teamMembers}
        onAssignmentChange={onAssignmentChange}
        onClose={() => setTeamEditJobId(null)}
        getConflict={getConflict}
        formatDate={formatDate}
        getDisplayTitle={getDisplayTitle}
      />
    </>
  );
};
